import { T } from '@transifex/react';
import { Heading, Text, VStack } from '@vidahealth/vida-ui';
import { useWindowDimensions, View } from 'react-native';

import { CoachImage } from '@/components/CoachImage';
import { coachDisplayName, coachName } from '@/components/CoachSummary';
import type { MatchedCoach } from '@/modules/api';
import { BottomSheet, CtaButton } from '@/modules/ui';

import { CheckIcon } from './CheckIcon';

const MD_BREAKPOINT = 520;
const SHEET_HORIZONTAL_PADDING = 24;

function CheckList({ items }: { items: string[] }) {
  return (
    <VStack space='sm'>
      {items.map((item) => (
        <View key={item} className='flex-row items-start gap-3'>
          <View className='mt-0.5'>
            <CheckIcon size={24} />
          </View>
          <Text size='md' className='flex-1 font-body text-typography-900'>
            {item}
          </Text>
        </View>
      ))}
    </VStack>
  );
}

/**
 * Private to CoachMatchingScreen — the full-bio bottom sheet (hero, About,
 * Qualifications, Licenses) with a "Continue with {name}" CTA that selects the
 * coach and advances. The hero is a fixed thumbnail at md+ and edge-to-edge
 * below, with the "Top coach" badge when this is the recommended coach.
 */
export function CoachBioSheet({
  coach,
  isOpen,
  isTopCoach = false,
  onClose,
  onContinue,
}: {
  coach: MatchedCoach | null;
  isOpen: boolean;
  isTopCoach?: boolean;
  onClose: () => void;
  onContinue: (coach: MatchedCoach) => void;
}) {
  const { width } = useWindowDimensions();
  const isMd = width >= MD_BREAKPOINT;
  if (!coach) return null;

  const licenseLabels = coach.licenses
    .map((l) => [l.type, l.state, l.number].filter(Boolean).join(' · '))
    .filter(Boolean);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <CtaButton onPress={() => onContinue(coach)} testID='coach-bio-continue'>
          <T _str='Continue with {name}' name={coachDisplayName(coach)} />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <CoachImage
          coach={coach}
          // `fit` (default) keeps the photo's natural ratio so the thumbnail
          // isn't cropped. md: capped at 300×200 (scaled to fit, no crop);
          // mobile: edge-to-edge (negative margins cancel the BottomSheet's
          // px-6/py-6).
          maxWidth={isMd ? 300 : undefined}
          maxHeight={isMd ? 200 : undefined}
          containerStyle={
            isMd
              ? { borderRadius: 24, alignSelf: 'flex-start' }
              : {
                  marginHorizontal: -SHEET_HORIZONTAL_PADDING,
                  marginTop: -SHEET_HORIZONTAL_PADDING,
                  width,
                }
          }
          showTopCoach={isTopCoach}
          badgePosition='right'
        />

        <VStack space='xs'>
          <Heading size='xl' className='text-primary-700'>
            {coachName(coach)}
          </Heading>
          {coach.title ? (
            <Text size='md' className='font-body text-typography-900'>
              {coach.title}
            </Text>
          ) : null}
        </VStack>

        {coach.bio ? (
          <VStack space='sm'>
            <Text size='md' weight='semibold' className='text-typography-900'>
              <T _str='About' />
            </Text>
            <Text size='md' className='text-typography-900'>
              {coach.bio}
            </Text>
          </VStack>
        ) : null}

        {coach.qualifications.length > 0 ? (
          <VStack space='sm'>
            <Text size='md' weight='semibold' className='text-typography-900'>
              <T _str='Qualifications' />
            </Text>
            <CheckList items={coach.qualifications} />
          </VStack>
        ) : null}

        {licenseLabels.length > 0 ? (
          <VStack space='sm'>
            <Text size='md' weight='semibold' className='text-typography-900'>
              <T _str='Licenses' />
            </Text>
            <CheckList items={licenseLabels} />
          </VStack>
        ) : null}
      </VStack>
    </BottomSheet>
  );
}
